import AddIcon from '@mui/icons-material/Add';
import PetsIcon from '@mui/icons-material/Pets';
import { Box, Button, Container, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SkeletonLoader from '../../../components/common/SkeletonLoader';
import PetProfile from '../../../happypaws-shared/src/Pets/models/PetProfile';
import { useDeletePet } from '../../../hooks/pets/useDeletePet';
import { useGetPets } from '../../../hooks/pets/useGetPets';
import ClientHeader from '../ClientHeader/ClientHeader';
import DeletePetDialog from './components/DeletePetDialog';
import PetCard from './components/PetCard';

const MyPets: React.FC = () => {
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [petToDelete, setPetToDelete] = useState<PetProfile | null>(null);

  const { mutate: deletePet, isPending } = useDeletePet();
  const { data: pets, isLoading: isPetsLoading } = useGetPets();

  // Action handlers
  // const handleViewDetails = (id: string) => {
  //   console.log('Viewing pet details:', id);
  // };

  const handleEdit = (id: string) => {
    const pet = pets?.find((p) => p.id === id);
    navigate(`/edit-pet/${id}`, { state: { pet } });
  };

  const handleBookSitter = (id: string) => {
    navigate('/dashboard', { state: { selectedPetId: id } });
  };

  // const handleSetPrimary = (id: string) => {
  //   setPets((prev) =>
  //     prev.map((pet) => ({
  //       ...pet,
  //       isPrimary: pet.id === id,
  //     }))
  //   );
  // };

  // const handleDuplicate = (id: string) => {
  //   const petToDuplicate = pets?.find((p) => p.id === id);
  //   if (!petToDuplicate) return;

  //   const duplicated: PetProfile = {
  //     ...petToDuplicate,
  //     id: Date.now().toString(),
  //     petName: `${petToDuplicate.petName} (Copy)`,
  //   };
  //   setPets((prev) => [...prev, duplicated]);
  // };

  const handleDeleteClick = (id: string) => {
    const pet = pets?.find((p) => p.id === id);
    if (pet) {
      setPetToDelete(pet);
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirm = () => {
    if (!petToDelete?.id) return;

    deletePet(petToDelete.id, {
      onSuccess: () => {
        setDeleteDialogOpen(false);
        setPetToDelete(null);
      },
    });
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPetToDelete(null);
  };

  const petList = useMemo(() => {
    if (pets?.length === 0) {
      return (
        // ADD return here
        <Box sx={{ textAlign: 'center', py: 8, px: 3, backgroundColor: 'white', borderRadius: 3 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: 'rgba(23, 139, 142, 0.1)',
              mb: 3,
            }}
          >
            <PetsIcon sx={{ fontSize: 40, color: '#178B8E' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#1A1A1A' }}>
            No pets added yet
          </Typography>
          <Typography variant="body1" sx={{ color: '#5A5A5A', mb: 3, maxWidth: 320, mx: 'auto' }}>
            Add your first pet to start booking sitters!
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/add-pet')}
            sx={{ textTransform: 'none', fontWeight: 600, px: 4, py: 1.5, fontSize: '1rem' }}
          >
            Add Your First Pet
          </Button>
        </Box>
      );
    }

    return (
      <Box>
        {pets?.map((pet) => (
          <PetCard
            key={pet.id}
            pet={pet}
            onEdit={handleEdit}
            onBookSitter={handleBookSitter}
            onDelete={handleDeleteClick}
          />
        ))}
      </Box>
    );
  }, [pets]);

  return (
    <Box sx={{ backgroundColor: '#FAF8F5', minHeight: '100vh' }}>
      <ClientHeader />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Page Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 4,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
              My Pets
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              Manage your furry friends' profiles
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/add-pet')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.2,
            }}
          >
            Add New Pet
          </Button>
        </Box>

        <SkeletonLoader isLoading={isPetsLoading} count={3}>
          {petList}
        </SkeletonLoader>
      </Container>

      {/* Delete Confirmation Dialog */}
      <DeletePetDialog
        open={deleteDialogOpen}
        petName={petToDelete?.petName || ''}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        isDeleting={isPending}
      />
    </Box>
  );
};

export default MyPets;
